import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Eyebrow } from "@/components/shared/Eyebrow";
import { Reveal } from "@/components/shared/Reveal";
import { CTA } from "@/components/home/CTA";
import { ProjectCard } from "@/components/shared/ProjectCard";
import { useService, useProjects } from "@/hooks/useContent";
import { useSeo } from "@/components/shared/useSeo";

export default function HizmetDetay() {
  const { slug } = useParams();
  const { data: service, isLoading, isError } = useService(slug);
  const { data: projects = [] } = useProjects();

  useSeo({
    title: service ? `${service.title} | Sistem Elektrik ve Mühendislik` : "Hizmet",
    description: service?.short_desc,
    canonical: `https://sistemmuhendislik.com/hizmetler/${slug}`,
  });

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-navy"><Loader2 className="w-8 h-8 text-cyan animate-spin" /></div>;
  if (isError || !service) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-navy text-white gap-4">
      <p>Hizmet bulunamadı.</p>
      <Link to="/hizmetler" className="text-cyan">Tüm Hizmetler</Link>
    </div>
  );

  const related = projects.filter((p) => p.services?.some((sv) => service.title.includes(sv) || sv.includes(service.title.split(" ")[0]))).slice(0, 3);
  const relatedList = related.length ? related : projects.slice(0, 3);

  return (
    <>
      <PageHero
        eyebrow={`HİZMET · ${service.number}`}
        title={service.title}
        subtitle={service.short_desc}
        image={service.image}
        crumb={[{ label: "Hizmetler", to: "/hizmetler" }, { label: service.title }]}
      />

      {/* Overview */}
      <section className="bg-light py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Eyebrow className="mb-6">GENEL BAKIŞ</Eyebrow>
            <Reveal>
              <p className="text-xl md:text-2xl text-ink font-head font-semibold leading-snug">{service.description}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-ink/10 bg-white p-8">
              <h3 className="font-head font-bold text-ink text-lg mb-5">Tipik Uygulamalar</h3>
              <ul className="space-y-3">
                {(service.applications || []).map((a) => (
                  <li key={a} className="flex gap-3 text-ink/70"><span className="text-cyan mt-1">—</span>{a}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="bg-dark text-white py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Eyebrow dark className="mb-6">YETKİNLİKLER</Eyebrow>
          <Reveal>
            <h2 className="font-head font-extrabold tracking-tight text-3xl md:text-5xl max-w-2xl leading-[1.05]">
              Kapsamlı teknik yetkinlikler.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-px">
            {(service.capabilities || []).map((c, i) => (
              <motion.div key={c} className="flex items-center gap-4 py-5 border-b border-white/10"
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.06 }}>
                <span className="w-8 h-8 shrink-0 flex items-center justify-center bg-cyan/10 text-cyan border border-cyan/30"><Check className="w-4 h-4" /></span>
                <span className="text-silver/90">{c}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related projects */}
      <section className="bg-light py-24 md:py-28">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <Eyebrow className="mb-4">ÖRNEK UYGULAMA ALANLARI</Eyebrow>
              <h2 className="font-head font-extrabold text-ink tracking-tight text-3xl md:text-4xl">Hizmet kapsamını temsil eden örnekler</h2>
            </div>
            <Link to="/projeler" className="group hidden md:inline-flex items-center gap-2 text-tech font-semibold">
              Tümü <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedList.map((p, i) => <ProjectCard key={p.slug} project={p} index={i} />)}
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}
